/**
 * AppLayout
 *
 * Top-level shell. Layout from the redesign mockup
 * (`shaka-deck-mockup-shell.html`):
 *
 *   ┌────────────── full-width header ──────────────┐
 *   │  brand · getaway · ⋯ · NFC · bell · user      │
 *   ├──────┬─────────────────────────────────────────┤
 *   │ rail │ main content                            │
 *   │ +    │                                          │
 *   │ panel│                                          │
 *   └──────┴─────────────────────────────────────────┘
 *
 * The header spans the entire viewport width — no longer wedged into
 * the right column next to the sidebar. The sidebar (rail + optional
 * panel) starts below it, so the brand wordmark + getaway switcher
 * read first regardless of where the rail is in its lifecycle.
 */

import { SidebarProvider } from '@/theme/components/ui/sidebar';
import { RailSidebar } from './sidebar/RailSidebar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { UpdateAvailableBanner } from '@/shared/components/UpdateAvailableBanner';
import { NfcCardScanPopover } from '@/shared/components/NfcCardScanPopover';
import { PermissionChangeGate } from '@/core/auth/PermissionChangeGate';
import { MobileDrawerAutoClose } from './MobileDrawerAutoClose';
import { AppHeader } from './AppHeader';
import { useRealtimeInvalidation } from '@/shared/hooks';
import { SyncProgressBanner } from '@/modules/integrations/bookinglayer/components/SyncProgress';
import { rememberLocationOverviewPath } from '@/shared/services/locationOverviewRouting';
import { useTenant } from '@/core/tenant';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// In-memory sidebar open state. Browser storage (cookies included) is
// prohibited workspace-wide, so the user's collapsed/expanded preference
// is held in a module-level variable instead of a cookie. ProtectedPage
// wraps every route in its own AppLayout, so the SidebarProvider mounts
// fresh on each navigation — without persisting this across mounts the
// sidebar would re-expand to the default after every link click. The
// module variable survives route changes for the session and resets on a
// full reload, which is acceptable for a UI layout preference.
let sidebarOpenMem = true;

function readSidebarOpen(): boolean {
  return sidebarOpenMem;
}

function writeSidebarOpen(open: boolean): void {
  sidebarOpenMem = open;
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  useRealtimeInvalidation();
  const { currentLocationId } = useTenant();
  const locationPath = useLocation();

  const isLocationCtx = !!currentLocationId;

  // Controlled sidebar state backed by the in-memory module variable so
  // the open/collapsed preference persists across the per-route remounts
  // of SidebarProvider without touching browser storage.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => readSidebarOpen());

  const handleSidebarOpenChange = (open: boolean) => {
    writeSidebarOpen(open);
    setSidebarOpen(open);
  };

  useEffect(() => {
    if (!isLocationCtx) {
      return;
    }

    rememberLocationOverviewPath(locationPath.pathname);
  }, [isLocationCtx, locationPath.pathname]);

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={handleSidebarOpenChange}
      style={
        {
          '--sidebar-width': '19rem',
          // 4rem = 64px = the rail-only width. When the user collapses
          // the sidebar the wrapper shrinks to this exact width so the
          // domain rail stays fully visible while the 240px sub-panel
          // animates out.
          '--sidebar-width-icon': '4rem',
        } as React.CSSProperties
      }
    >
      <MobileDrawerAutoClose />
      {/* h-svh + overflow-hidden on the wrapper makes <main> the scroll
          container instead of <body>. Without this the sticky AppHeader
          drifts when the page content is taller than the viewport (the
          flex-direction:column outer + body-level scroll combination
          breaks position:sticky on the way down). With main scrolling,
          the header stays anchored to the top of the wrapper at all
          times. */}
      <div className="h-svh w-full flex flex-col overflow-hidden">
        {/* Impersonation banner spans the full width above the header
            so it's always visible while admin is impersonating. */}
        <ImpersonationBanner />
        {/* Renders only when a fresher deck deploy has shipped while
            this tab is still on the previous bundle (compares the
            bundled VITE_BUILD_VERSION against /healthz?version). */}
        <UpdateAvailableBanner />
        <AppHeader />
        <div className="flex flex-1 min-h-0 w-full">
          <RailSidebar />
          <main className="flex-1 bg-card overflow-y-auto overflow-x-hidden">
            {children}
          </main>
        </div>
        {/* Global popovers mount once at the shell level so they fire
            from any surface. */}
        <NfcCardScanPopover />
        {/* Blocks (downgrade) or offers (upgrade) a refresh when this
            user's effective permissions change mid-session, so the UI
            never lets them act on access they no longer have. */}
        <PermissionChangeGate />
        {/* Bookinglayer sync banner — visible on every page except
            the BL status page itself, where the inline component
            takes over. Dismiss state is held in memory per job id for
            the session (in-memory module variable, resets on reload). */}
        <SyncProgressBanner />
      </div>
    </SidebarProvider>
  );
}
